import {
  Button,
  ButtonGroup,
  Divider,
  GridItem,
  IconButton,
  Spacer,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { faRulerHorizontal } from '@fortawesome/free-solid-svg-icons';
import { sum } from 'lodash';
import { useTranslation } from 'react-i18next';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { ViewerMode } from 'src/components/common/viewer-mode';
import { Pagination } from 'src/components/viewers/SheetsViewerV2/components/Pagination';
import { PDFOpacitySlider } from 'src/components/viewers/SheetsViewerV2/components/PDFOpacitySlider';
import { SheetCalibration } from 'src/components/viewers/SheetsViewerV2/components/SheetCalibration';
import SheetShapeLabelsButton from 'src/components/viewers/SheetsViewerV2/components/SheetShapeLabelsButton';
import WindLoadZonesToggleButton from 'src/components/viewers/SheetsViewerV2/components/WindLoadZonesToggleButton';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { useUserTenant } from 'src/services/auth-info';
import SnappingButton from 'src/components/viewers/SheetsViewerV2/components/SnappingButton';

export function SheetsViewerV2Interactions({
  readOnly,
}: {
  readOnly?: boolean;
}) {
  const {
    viewerMode,
    pagination,
    activeSheet,
    setActiveTool,
    measurement,
    calibration,
    visibilityManager: {
      setIsolatedElements,
      setHiddenElements,
      hiddenElements,
      isolatedElements,
    },
  } = useSheetViewerV2();
  const { t } = useTranslation('project');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const numHiddenElements = sum(
    Object.values(hiddenElements).map((v) => v.length)
  );
  const numIsolatedElements = sum(
    Object.values(isolatedElements).map((v) => v.length)
  );

  const activeIconColor = useColorModeValue('teal.500', 'teal.200');

  const { isProtan } = useUserTenant();

  if (viewerMode !== ViewerMode.SheetsV2 || activeSheet?.isLoading) {
    return null;
  }

  return (
    <GridItem
      area="viewer-interactions"
      display="flex"
      pointerEvents="none"
      paddingTop={2}
      zIndex={999}
      sx={{
        '> *:not(:empty)': {
          pointerEvents: 'auto',
        },
        '@media (max-width: 1200px)': {
          paddingRight: 2,
        },
      }}
    >
      <Pagination {...pagination} />
      <ButtonGroup mr={2}>
        {!readOnly && <SheetCalibration />}
        <Divider borderColor={borderColor} orientation="vertical" />
        <PDFOpacitySlider />
        <SheetShapeLabelsButton />
        {isProtan && <WindLoadZonesToggleButton />}
        <Divider borderColor={borderColor} orientation="vertical" />
        <SnappingButton />
        <Divider borderColor={borderColor} orientation="vertical" />
        <Tooltip
          label={
            calibration.existing
              ? t('viewer-interactions.measure-toggle')
              : t('viewer-interactions.calibration-required')
          }
        >
          <IconButton
            aria-label="Measure tool"
            icon={<SparkelIcon icon={faRulerHorizontal} />}
            isRound
            isActive={measurement.isMeasuring}
            variant="solid-no-transparent"
            colorScheme="gray"
            isDisabled={!calibration.existing}
            size="xs"
            onClick={() => {
              if (measurement.isMeasuring) {
                setActiveTool(ActiveTool.Default);
              } else {
                setActiveTool(ActiveTool.Measurement);
              }
            }}
          />
        </Tooltip>
      </ButtonGroup>
      <Spacer />
      <ButtonGroup>
        {numIsolatedElements > 0 ? (
          <Tooltip label={t('viewer-interactions.clear-isolated')}>
            <Button
              colorScheme="teal"
              variant="toggle"
              isActive
              size="xs"
              color={activeIconColor}
              onClick={() => setIsolatedElements({})}
            >
              {t('viewer-interactions.isolated-elements', {
                numIsolatedElements,
              })}
            </Button>
          </Tooltip>
        ) : null}
        {numHiddenElements > 0 ? (
          <Tooltip label="Clear hidden elements">
            <Button
              colorScheme="teal"
              variant="toggle"
              isActive
              size="xs"
              color={activeIconColor}
              onClick={() => setHiddenElements({})}
            >
              {t('viewer-interactions.hidden-elements', { numHiddenElements })}
            </Button>
          </Tooltip>
        ) : null}
      </ButtonGroup>
    </GridItem>
  );
}
